import { useEffect, useState } from "react";
import { HelpCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { API } from "../App";

// The "?" panel. Examples are generated for the round being looked at — their
// game, their item count, their memorise allowance — so the numbers always
// match what's on screen.
export const RatingPanel = ({
  mode = "words",
  itemsCount = 30,
  difficulty,
  memorizeAllowanceSeconds,
  label = "How your rating works",
  trigger,
}) => {
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        let url = `${API}/scoring/explain?mode=${mode}&items_count=${itemsCount}`;
        if (difficulty && mode === "words") url += `&difficulty=${difficulty}`;
        if (memorizeAllowanceSeconds) url += `&memorize_allowance_seconds=${memorizeAllowanceSeconds}`;
        const res = await fetch(url);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setInfo(data);
      } catch {
        /* panel is explanatory only */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, mode, itemsCount, difficulty, memorizeAllowanceSeconds]);

  const isWM = mode === "working_memory";
  const perItem = info?.target_per_item_label;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <button
            type="button"
            aria-label={label}
            data-testid="rating-help-trigger"
            className="inline-flex items-center justify-center w-6 h-6 rounded-full border border-border text-muted-foreground hover:text-primary hover:border-primary transition-colors"
          >
            <HelpCircle className="w-3.5 h-3.5" />
          </button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="rating-help-panel">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">How your rating works</DialogTitle>
          <DialogDescription className="sr-only">
            What the rating means, how target pace affects it, and examples for this round.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm text-muted-foreground">
          {isWM ? (
            <ul className="space-y-2 list-disc pl-5">
              <li>
                <span className="text-foreground font-medium">The level you reach is your rating.</span>{" "}
                Level 10 scores <span className="text-foreground font-medium">100</span>.
              </li>
              <li>There's no clock in this game, so nothing is added or taken away for speed.</li>
              <li>
                <span className="text-foreground font-medium">150 is the top of the scale.</span>{" "}
                100 means every item remembered at target pace — amazing by any normal
                standard. Above 100 is world-class.
              </li>
            </ul>
          ) : (
            <ul className="space-y-2 list-disc pl-5">
              <li>
                <span className="text-foreground font-medium">Every item you remember counts.</span>{" "}
                Remember them all at the{" "}
                <span className="text-foreground font-medium">
                  target pace{perItem ? ` (${perItem})` : ""}
                </span>{" "}
                and you score <span className="text-foreground font-medium">100</span>.
              </li>
              <li>
                <span className="text-foreground font-medium">Beat target pace and you score above 100.</span>{" "}
                Go slower and you score less — never below three-quarters of what you remembered.
              </li>
              <li>How fast you memorise counts about six times more than how fast you recall.</li>
              <li>
                <span className="text-foreground font-medium">150 is the top of the scale.</span>{" "}
                100 means every item remembered at target pace — amazing by any normal
                standard. Above 100 is world-class.
              </li>
            </ul>
          )}

          {info?.baseline_line && (
            <p className="text-foreground/90" data-testid="rating-help-baseline">
              {info.baseline_line}
            </p>
          )}

          {info?.examples?.length > 0 && (
            <div className="pt-2">
              <p className="text-foreground font-medium mb-2">{info.context_label}</p>
              <div className="space-y-1 font-mono text-xs sm:text-sm" data-testid="rating-help-examples">
                {info.examples.map((ex, i) => (
                  <div key={i} data-testid={`rating-help-example-${i}`}>
                    {ex.text} → <span className="text-primary font-bold">{ex.rating}</span>
                  </div>
                ))}
              </div>
              {info.closing_line && (
                <p className="mt-3 text-xs italic" data-testid="rating-help-closing">
                  {info.closing_line}
                </p>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RatingPanel;
